import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import TicketsService, {
  CreateTicketPayload,
  SendTicketMessagePayload,
  TicketDetail,
  TicketListQuery,
  TicketPriority,
} from '@/services/TicketsService';

export const TICKETS_QUERY_KEY = ['tickets'];

export const TICKETS_LIST_QUERY_KEY = (query: TicketListQuery) => [
  ...TICKETS_QUERY_KEY,
  'list',
  query,
];

export const TICKET_DETAIL_QUERY_KEY = (ticketId: number) => [
  ...TICKETS_QUERY_KEY,
  'detail',
  ticketId,
];

export function useTicketsList(
  query: TicketListQuery,
  enabled: boolean = true,
) {
  const { data, isFetching, refetch, error } = useQuery({
    queryKey: TICKETS_LIST_QUERY_KEY(query),
    queryFn: () => TicketsService.listTickets(query),
    enabled,
  });

  return {
    tickets: data?.data ?? [],
    total: data?.total ?? 0,
    limit: data?.limit ?? query.limit ?? 20,
    offset: data?.offset ?? query.offset ?? 0,
    isLoading: isFetching,
    error,
    refetch,
  };
}

export function useTicketDetail(
  ticketId: number | null,
  enabled: boolean = true,
) {
  const resolvedTicketId = ticketId ?? 0;

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: TICKET_DETAIL_QUERY_KEY(resolvedTicketId),
    queryFn: () => TicketsService.getTicketDetail(resolvedTicketId),
    enabled: enabled && !!ticketId,
  });

  return {
    ticket: data,
    isLoading: isFetching,
    error,
    refetch,
  };
}

function storeTicketInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  ticket: TicketDetail,
) {
  queryClient.setQueryData(TICKET_DETAIL_QUERY_KEY(ticket.id), ticket);
  queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: CreateTicketPayload) =>
      TicketsService.createTicket(payload),
    onSuccess: (ticket) => {
      storeTicketInCache(queryClient, ticket);
    },
  });

  return mutation;
}

export function useSendTicketMessage() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      ticketId,
      payload,
    }: {
      ticketId: number;
      payload: SendTicketMessagePayload;
    }) => TicketsService.sendMessage(ticketId, payload),
    onSuccess: (ticket) => {
      storeTicketInCache(queryClient, ticket);
    },
  });

  return mutation;
}

export function useFinalizeTicket() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (ticketId: number) => TicketsService.finalizeTicket(ticketId),
    onSuccess: (ticket) => {
      storeTicketInCache(queryClient, ticket);
    },
  });

  return mutation;
}

export function useReopenTicket() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (ticketId: number) => TicketsService.reopenTicket(ticketId),
    onSuccess: (ticket) => {
      storeTicketInCache(queryClient, ticket);
    },
  });

  return mutation;
}

export function useUpdateTicketPriority() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      ticketId,
      priority,
    }: {
      ticketId: number;
      priority: TicketPriority;
    }) => TicketsService.updateTicketPriority(ticketId, { priority }),
    onSuccess: (ticket) => {
      storeTicketInCache(queryClient, ticket);
    },
  });

  return mutation;
}
